import React, { useMemo, useState, useEffect } from 'react';
import {
  RiCloseLine, RiTimeLine, RiPlayFill, RiCheckboxCircleLine,
  RiTaskLine, RiUserLine, RiCalendarLine, RiEditLine, RiSaveLine,
  RiSparkling2Line,
} from 'react-icons/ri';
import api from '../../utils/api';

// Brand palette — derived from the Nexcess logo gradient.
const BRAND = {
  blue: '#0984e3',
  cyan: '#00b0ff',
  purple: '#7c3aed',
  magenta: '#d500f9',
  gradient: 'linear-gradient(135deg,#0984e3 0%,#00b0ff 55%,#d500f9 100%)',
  gradientSoft: 'linear-gradient(135deg,rgba(9,132,227,0.08),rgba(213,0,249,0.08))',
};

function fmtDuration(ms) {
  if (!ms || ms < 0) return '0m';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function fmtTotal(ms) {
  if (!ms || ms < 0) return '0h 0m';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

function userOf(it) {
  return it.user?.name || it.user?.username || it.user?.email_id
       || it.userName || it.userEmail || '—';
}

function initialsOf(name) {
  return (name || 'U')
    .split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) || 'U';
}

export default function AttendanceDayModal({
  dateStr,
  items: itemsProp = [],
  onClose,
  showUser = false,
  groupByUser = false,
  editable = false,
  onItemUpdated,
}) {
  const [items, setItems] = useState(itemsProp);
  useEffect(() => { setItems(itemsProp); }, [itemsProp]);

  const dateLabel = useMemo(() => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }, [dateStr]);

  const sorted = useMemo(
    () => [...items].sort((a, b) => new Date(a.start_time || 0) - new Date(b.start_time || 0)),
    [items],
  );

  const totals = useMemo(() => {
    let totalMs = 0;
    let working = 0;
    const userIds = new Set();
    for (const it of items) {
      if (!it.start_time) continue;
      const start = new Date(it.start_time).getTime();
      const end = it.end_time ? new Date(it.end_time).getTime() : Date.now();
      totalMs += Math.max(0, end - start);
      if (!it.end_time) working++;
      const uid = it.user_id || it.user?.id || it.user?._id;
      if (uid) userIds.add(uid);
    }
    return { totalMs, working, users: userIds.size };
  }, [items]);

  const grouped = useMemo(() => {
    if (!groupByUser) return null;
    const map = new Map();
    for (const it of sorted) {
      const uid = it.user_id || it.user?.id || it.user?._id || 'unknown';
      if (!map.has(uid)) map.set(uid, { name: userOf(it), items: [], totalMs: 0 });
      const g = map.get(uid);
      g.items.push(it);
      if (it.start_time) {
        const start = new Date(it.start_time).getTime();
        const end = it.end_time ? new Date(it.end_time).getTime() : Date.now();
        g.totalMs += Math.max(0, end - start);
      }
    }
    return [...map.values()].sort((a, b) => b.totalMs - a.totalMs);
  }, [sorted, groupByUser]);

  const taskTotals = useMemo(() => {
    const map = new Map();
    for (const it of sorted) {
      const raw = (it.task || '').trim();
      const key = raw.toLowerCase() || '__untitled__';
      if (!map.has(key)) {
        map.set(key, { name: raw || 'Untitled', totalMs: 0, count: 0, hasOpen: false });
      }
      const g = map.get(key);
      if (it.start_time) {
        const start = new Date(it.start_time).getTime();
        const end = it.end_time ? new Date(it.end_time).getTime() : Date.now();
        g.totalMs += Math.max(0, end - start);
      }
      g.count += 1;
      if (!it.end_time) g.hasOpen = true;
    }
    return [...map.values()].sort((a, b) => b.totalMs - a.totalMs);
  }, [sorted]);

  // Only show the summary if at least one task is repeated across sessions.
  const repeatedTasks = taskTotals.filter((t) => t.count > 1);
  const showTaskSummary = repeatedTasks.length > 0;

  const handleRename = (id, nextTask) => {
    setItems((prev) => prev.map((it) => (it.id || it._id) === id ? { ...it, task: nextTask } : it));
    onItemUpdated?.({ id, task: nextTask });
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/40 backdrop-blur-md flex items-center justify-center p-4">
      <div className="relative bg-white rounded-[28px] w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden
                      border border-[#EEF2F6]
                      shadow-[0_24px_72px_-12px_rgba(9,132,227,0.28),0_2px_4px_rgba(15,23,42,0.06)]">

        {/* Brand-gradient top sheen */}
        <div className="absolute top-0 inset-x-0 h-[3px] z-10" style={{ background: BRAND.gradient }} />

        {/* Decorative blobs */}
        <div className="absolute -top-24 -right-20 w-64 h-64 rounded-full opacity-[0.16] blur-3xl pointer-events-none"
             style={{ background: `radial-gradient(circle,${BRAND.magenta} 0%,transparent 70%)` }} />
        <div className="absolute -bottom-28 -left-24 w-72 h-72 rounded-full opacity-[0.10] blur-3xl pointer-events-none"
             style={{ background: `radial-gradient(circle,${BRAND.blue} 0%,transparent 70%)` }} />

        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="relative px-7 pt-7 pb-5">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white border border-[#EEF2F6] text-[#475569]
                       hover:text-[#0984e3] hover:border-[#0984e3]/40 hover:shadow-[0_6px_16px_-6px_rgba(9,132,227,0.35)]
                       active:scale-95 transition-all flex items-center justify-center cursor-pointer"
            aria-label="Close"
          >
            <RiCloseLine size={18} />
          </button>

          <div className="flex items-center gap-3.5">
            {/* Brand-gradient calendar icon */}
            <div className="relative w-11 h-11 rounded-2xl flex items-center justify-center text-white flex-shrink-0
                            shadow-[0_8px_20px_-6px_rgba(9,132,227,0.55)]"
                 style={{ background: BRAND.gradient }}>
              <RiCalendarLine size={19} />
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-white border border-[#EEF2F6] flex items-center justify-center">
                <RiSparkling2Line size={6} style={{ color: BRAND.magenta }} />
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#64748B]">
                Attendance details
              </p>
              <h2 className="font-bold text-[20px] text-[#0F172A] tracking-tight leading-tight mt-1 truncate">
                {dateLabel}
              </h2>
            </div>
          </div>

          {/* Stat cards */}
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            <StatTile
              icon={RiTimeLine}
              label="Total"
              value={fmtTotal(totals.totalMs)}
              tone={BRAND.blue}
              tint="#EBF6FE"
            />
            <StatTile
              icon={RiTaskLine}
              label={items.length === 1 ? 'Entry' : 'Entries'}
              value={`${items.length}`}
              tone={BRAND.cyan}
              tint="#E5F6FE"
            />
            {showUser && totals.users > 0 ? (
              <StatTile
                icon={RiUserLine}
                label={totals.users === 1 ? 'Employee' : 'Employees'}
                value={`${totals.users}`}
                tone={BRAND.purple}
                tint="#F1EBFE"
              />
            ) : (
              <StatTile
                icon={RiCheckboxCircleLine}
                label="Finished"
                value={`${items.length - totals.working}`}
                tone={BRAND.purple}
                tint="#F1EBFE"
              />
            )}
            <StatTile
              icon={RiPlayFill}
              label="Working"
              value={`${totals.working}`}
              tone={BRAND.magenta}
              tint="#FCEBFB"
              pulse={totals.working > 0}
            />
          </div>
        </div>

        {/* ── Body ──────────────────────────────────────────────────────── */}
        <div className="relative flex-1 overflow-y-auto bg-gradient-to-b from-white to-[#F8FAFC]">

          {/* Per-task totals (only when duplicates exist) */}
          {showTaskSummary && (
            <div className="px-7 pt-5 pb-4 border-y border-[#EEF2F6] bg-white/60 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#64748B] flex items-center gap-1.5">
                  <RiTaskLine size={11} style={{ color: BRAND.blue }} />
                  Task totals
                </p>
                <span className="text-[10px] font-semibold text-[#94A3B8]">
                  {repeatedTasks.length} repeated {repeatedTasks.length === 1 ? 'task' : 'tasks'}
                </span>
              </div>
              <div className="space-y-2">
                {repeatedTasks.map((t) => (
                  <div
                    key={t.name}
                    className="group relative flex items-center gap-3 pl-3 pr-3.5 py-2.5 rounded-xl
                               bg-white border border-[#EEF2F6]
                               hover:border-[#0984e3]/30 hover:shadow-[0_8px_22px_-10px_rgba(9,132,227,0.28)]
                               transition-all overflow-hidden"
                  >
                    {/* Brand accent bar */}
                    <span
                      className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full"
                      style={{
                        background: t.hasOpen
                          ? `linear-gradient(180deg,${BRAND.magenta},${BRAND.cyan})`
                          : BRAND.gradient,
                      }}
                    />
                    <div className="flex-1 min-w-0 pl-1">
                      <p className="text-[#0F172A] text-sm font-semibold truncate">
                        {t.name}
                      </p>
                      <p className="text-[10.5px] text-[#64748B] mt-0.5 flex items-center gap-1">
                        <RiSparkling2Line size={9} style={{ color: BRAND.purple }} />
                        {t.count} sessions combined
                        {t.hasOpen && (
                          <span
                            className="inline-flex items-center gap-1 ml-1.5 px-1.5 py-0.5 rounded-full font-bold"
                            style={{ background: `${BRAND.magenta}12`, color: BRAND.magenta }}
                          >
                            <span className="w-1 h-1 rounded-full animate-pulse"
                                  style={{ background: BRAND.magenta }} />
                            Live
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[15px] font-extrabold tabular-nums tracking-tight bg-clip-text text-transparent"
                         style={{ backgroundImage: BRAND.gradient }}>
                        {fmtTotal(t.totalMs)}
                      </p>
                      <p className="text-[9.5px] uppercase tracking-wider text-[#94A3B8] font-bold mt-0.5">
                        total
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-session list */}
          {items.length === 0 ? (
            <div className="py-16 text-center">
              <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                   style={{ background: BRAND.gradientSoft }}>
                <RiCalendarLine size={20} style={{ color: BRAND.blue }} />
              </div>
              <p className="text-[#64748B] text-sm font-medium">No entries on this day.</p>
            </div>
          ) : groupByUser ? (
            <div className="divide-y divide-[#F1F5F9]">
              {grouped.map((g, gi) => (
                <div key={gi} className="px-7 py-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[#0F172A] text-sm font-bold flex items-center gap-2.5">
                      <span className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-[11px] font-extrabold
                                       shadow-[0_6px_14px_-4px_rgba(9,132,227,0.45)]"
                            style={{ background: BRAND.gradient }}>
                        {initialsOf(g.name)}
                      </span>
                      {g.name}
                    </p>
                    <span className="text-[12px] font-extrabold tabular-nums bg-clip-text text-transparent"
                          style={{ backgroundImage: BRAND.gradient }}>
                      {fmtTotal(g.totalMs)}
                    </span>
                  </div>
                  <div className="space-y-2 pl-10">
                    {g.items.map((it, i) => (
                      <EntryRow
                        key={it.id || it._id || i}
                        it={it}
                        editable={editable}
                        onRenamed={handleRename}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-7 py-4 space-y-2">
              {sorted.map((it, i) => (
                <div key={it.id || it._id || i}>
                  {showUser && (
                    <p className="text-[11px] text-[#64748B] mb-1.5 flex items-center gap-1.5 font-medium">
                      <RiUserLine size={10} style={{ color: BRAND.purple }} />
                      {userOf(it)}
                    </p>
                  )}
                  <EntryRow it={it} editable={editable} onRenamed={handleRename} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, tone, tint, pulse }) {
  return (
    <div className="relative rounded-xl px-3 py-2.5 bg-white border border-[#EEF2F6] overflow-hidden
                    hover:shadow-[0_6px_16px_-8px_rgba(9,132,227,0.25)] transition-shadow">
      <span className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: tone, opacity: 0.85 }} />
      <div className="flex items-center gap-2.5">
        <div className="relative w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ background: tint, border: `1px solid ${tone}25` }}>
          {pulse && (
            <span className="absolute inset-0 rounded-lg animate-ping" style={{ background: `${tone}25` }} />
          )}
          <Icon size={13} style={{ color: tone }} className="relative" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#94A3B8] leading-none">
            {label}
          </p>
          <p className="text-[#0F172A] font-extrabold text-[15px] tabular-nums leading-tight mt-1 tracking-tight">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

function EntryRow({ it, editable, onRenamed }) {
  const id = it.id || it._id;
  const isOpen = !it.end_time;
  const start = it.start_time ? new Date(it.start_time).getTime() : 0;
  const end = isOpen ? Date.now() : (it.end_time ? new Date(it.end_time).getTime() : 0);
  const ms = Math.max(0, end - start);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(it.task || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const startEdit = () => { setDraft(it.task || ''); setError(''); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setError(''); };
  const save = async () => {
    const next = draft.trim();
    if (!next) { setError('Task name cannot be empty.'); return; }
    if (next === (it.task || '')) { setEditing(false); return; }
    setSaving(true);
    setError('');
    try {
      await api.patch(`/attendance/${id}`, { task: next });
      onRenamed?.(id, next);
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to rename. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`group relative rounded-xl px-3 py-2.5 border transition-all
                     ${isOpen
                       ? 'border-[#fcd34d]/50 bg-gradient-to-r from-[#FFFBEB] to-white'
                       : 'border-[#EEF2F6] bg-white hover:border-[#0984e3]/25'}
                     hover:shadow-[0_4px_14px_-8px_rgba(9,132,227,0.25)]`}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
             style={{
               background: isOpen ? 'rgba(245,158,11,0.10)' : `${BRAND.blue}10`,
               border: `1px solid ${isOpen ? 'rgba(245,158,11,0.25)' : `${BRAND.blue}25`}`,
             }}>
          {isOpen
            ? <RiPlayFill size={13} className="text-[#f59e0b]" />
            : <RiCheckboxCircleLine size={13} style={{ color: BRAND.blue }} />}
        </div>
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); save(); }
                    if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                  }}
                  autoFocus
                  disabled={saving}
                  placeholder="Task name"
                  className="input-field text-sm flex-1 min-w-[120px]"
                  style={{ padding: '0.4rem 0.7rem' }}
                />
                <button
                  onClick={save}
                  disabled={saving || !draft.trim()}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-white text-xs font-bold cursor-pointer
                             shadow-[0_4px_12px_-4px_rgba(9,132,227,0.5)] hover:shadow-[0_6px_16px_-4px_rgba(213,0,249,0.5)]
                             disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  style={{ background: BRAND.gradient }}
                  aria-label="Save task name"
                >
                  {saving
                    ? <span className="w-3 h-3 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                    : <RiSaveLine size={12} />}
                  Save
                </button>
                <button
                  onClick={cancelEdit}
                  disabled={saving}
                  className="p-1.5 rounded-lg text-[#64748B] hover:bg-[#F3F4F6] cursor-pointer disabled:opacity-50"
                  aria-label="Cancel"
                >
                  <RiCloseLine size={13} />
                </button>
              </div>
              {error && <p className="text-[11px] text-red-500 font-medium">{error}</p>}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-[#0F172A] text-sm font-semibold truncate">
                  {it.task || '—'}
                </p>
                {isOpen ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full
                                   text-[#b45309] bg-amber-100 border border-amber-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    Working
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{ color: BRAND.blue, background: `${BRAND.blue}10`, border: `1px solid ${BRAND.blue}25` }}>
                    Finished
                  </span>
                )}
                {editable && (
                  <button
                    onClick={startEdit}
                    className="p-1 rounded-md transition-all cursor-pointer
                               text-[#94A3B8] hover:text-[#0984e3] hover:bg-[#EBF6FE] opacity-0 group-hover:opacity-100"
                    aria-label="Rename task"
                    title="Rename task"
                  >
                    <RiEditLine size={12} />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-[#64748B] mt-1 flex-wrap">
                <span className="flex items-center gap-1">
                  <RiTimeLine size={10} />
                  {fmtTime(it.start_time)}{' '}
                  <span className="text-[#CBD5E1]">→</span>{' '}
                  {isOpen ? <span className="text-[#f59e0b] font-semibold">now</span> : fmtTime(it.end_time)}
                </span>
                <span className="font-extrabold tabular-nums bg-clip-text text-transparent"
                      style={{ backgroundImage: BRAND.gradient }}>
                  {fmtDuration(ms)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
